import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { KontrollterminStatus } from './projektaufgabe.service';

// Indicator Symbol Mapping
export const INDICATOR_SYMBOL_MAP = {
  '1': '🟦',    // Rectangle symbol
  '2': '🔶',    // Polygon symbol  
  '3': '🔵',    // Circle symbol
  '4': '🔺',    // Triangle symbol
  '6': '📄',    // Paper symbol
  '7': '📋',    // Layer symbol
  'motten': '🦋',
  'ameisen': '🐜', 
  'maus': '🐭',
  'ratte': '🐀',
  'schabe': '🪳',
  'fliege': '🪰',
  'muecke': '🦟',
  'spinne': '🕷️',
  'kaefer': '🐞',
  'milbe': '🦠',
  'silberfisch': '🐟',
  'holz': '🪵',
  'wasser': '💧',
  'temperatur': '🌡️',
  'licht': '💡',
  'lueftung': '🌬️',
  'reinigung': '🧼',
  'struktur': '🏗️',
  'allgemein': '📋'
};

export interface Kontrolltermin {
  id: string;
  einsatznummer: number;
  standort_id: string;
  projektaufgabe_id: string;
  kunde_id: string;
  techniker_id?: string;
  objektstruktur_indikatoren?: any; // JSON array
  anmerkungen?: string;
  soll_zeit?: number;
  ist_zeit?: number;
  monat_plan?: string;
  wochentag_plan?: string;
  einsatztyp: string;
  datum_durchfuerung?: string;
  status_id: number;
  global_befall_status_id?: number;
  global_befall?: number;
  zusatz_massnahme?: string;
  freigabe_id?: string;
  created_at?: string;
  updated_at?: string;
  
  // Relationships
  projektaufgabe?: Projektaufgabe;
  standort?: Standort;
  kunde?: Kunde;
  techniker?: Mitarbeiter;
  freigabe?: Freigabe;
  status?: KontrollterminStatus;
  kontrollIndikatoren?: KontrollIndikator[];
  kontroll_indikatoren?: KontrollIndikator[]; // Backend snake_case naming
}

export interface KontrollIndikator {
  id: string;
  gebaeude_id: string;
  gebaeude_name: string;
  sektion_id: string;
  sektion_name: string;
  room_id: string;
  room_name: string;
  indikator_id: string;
  indikator_name: string;
  wert?: number;
  befall?: number;
  befall_status_id?: number;
  problem?: string;
  massnahme?: string;
  checked?: string;
  checked_by?: string;
  anmerkung?: string;
  interne_notiz?: string;
  kontrolltermin_id: string;
  created_at?: string;
  updated_at?: string;
  
  // Relationships
  kontrolltermin?: Kontrolltermin;
  gebaeude?: Gebaeude;
  sektion?: Sektion;
  room?: Raum;
  indikator?: RoomIndikator;
  checkedBy?: Mitarbeiter;
}

export interface Freigabe {
  id: string;
  kontrolltermin_id: string;
  freigabe_durch: string;
  freigegeben: number;
  freigabe_datum: string;
  anmerkungen_freigabe?: string;
  versand_datum?: string;
  auswertung_link?: string;
  bericht_link?: string;
  created_at?: string;
  updated_at?: string;
  
  // Relationships
  kontrolltermin?: Kontrolltermin;
  freigabeDurch?: Mitarbeiter;
}

export interface Projektaufgabe {
  id: string;
  beschreibung: string;
  wiederkehrend: number;
  periodizitaet: number;
  status_id: number;
  kunde_id: string;
  techniker_id?: string;
  serie_start?: string;
  serie_ende?: string;
  anmerkungen?: string;
  soll_zeit?: number;
  monat_plan?: string;
  wochentag_plan?: string;
  anmeldung_angefordert?: string;
  standort_id?: string;
  einsatztyp: string;
  created_by?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
  
  // Relationships
  kunde?: Kunde;
  techniker?: Mitarbeiter;
  standort?: Standort;
  createdBy?: Account;
  updatedBy?: Account;
  kontrolltermine?: Kontrolltermin[];
}

export interface Standort {
  id: string;
  name: string;
  nummer?: string;
  kunde_id: string;
  bericht_email?: string; // E-Mail-Adresse für Berichtsversand
  created_at?: string;
  updated_at?: string;
  
  // Relationships
  kunde?: Kunde;
}

export interface Kunde {
  id: string;
  suchbegriff: string;
  status_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Mitarbeiter {
  id: string; // UUID from database
  name: string;
  email?: string;
  is_techniker?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Gebaeude {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface Sektion {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface Raum {
  id: string;
  name: string;
  created_at?: string;
  updated_at?: string;
}

export interface RoomIndikator {
  id: string;
  name: string;
  schwellenwert?: number;
  einheit?: string;
  mehr_weniger?: string;
  current_value?: number;
  status?: boolean;
  qr_code?: string;
  position_x?: number;
  position_y?: number;
  bemerkung?: string;
  indikator_number?: number;
  number_assigned_at?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  
  // Nested relationship to base Indikator
  indikator?: Indikator;
}

export interface Indikator {
  id: number;
  code: string;
  beschreibung: string;
  farbe?: string;
  symbol_id?: number;
  anzahl_system_typen?: number;
  aktive_systeme?: number;
  zusatz_detail?: string;
  einheit?: string;
  schwellenwert?: number;
  status_id?: number;
}

export interface Account {
  id: string;
  email: string;
  created_at?: string;
  updated_at?: string;
}

@Injectable({
  providedIn: 'root'
})
export class KontrollterminService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  // Get all kontrolltermine with optional filtering
  getKontrolltermine(
    status_id?: number,
    month?: string,
    standort_id?: string,
    kunde_id?: string,
    techniker_id?: string,
    einsatztyp?: string,
    date_from?: string,
    date_to?: string,
    search?: string,
    page?: number,
    limit?: number,
    global_befall?: number
  ): Observable<Kontrolltermin[]> {
    let params = new HttpParams();
    
    if (status_id !== undefined) params = params.set('status_id', status_id.toString());
    if (month) params = params.set('month', month);
    if (standort_id) params = params.set('standort_id', standort_id);
    if (kunde_id) params = params.set('kunde_id', kunde_id);
    if (techniker_id) params = params.set('techniker_id', techniker_id);
    if (einsatztyp) params = params.set('einsatztyp', einsatztyp);
    if (date_from) params = params.set('date_from', date_from);
    if (date_to) params = params.set('date_to', date_to);
    if (search) params = params.set('search', search);
    if (page !== undefined) params = params.set('page', page.toString());
    if (limit !== undefined) params = params.set('limit', limit.toString());
    if (global_befall !== undefined) params = params.set('global_befall', global_befall.toString());
    
    // Include related data - backend loads relationships automatically
    // params = params.set('include', 'kunde,standort,techniker,projektaufgabe');

    return this.http.get<Kontrolltermin[]>(`${this.apiUrl}/kontrolltermin`, { params });
  }

  // Get kontrolltermin by ID
  getKontrolltermin(id: string): Observable<Kontrolltermin> {
    // Backend loads relationships automatically
    return this.http.get<Kontrolltermin>(`${this.apiUrl}/kontrolltermin/${id}`);
  }

  // Create new kontrolltermin
  createKontrolltermin(kontrolltermin: Partial<Kontrolltermin>): Observable<Kontrolltermin> {
    return this.http.post<Kontrolltermin>(`${this.apiUrl}/kontrolltermin`, kontrolltermin);
  }

  // Update kontrolltermin
  updateKontrolltermin(id: string, kontrolltermin: Partial<Kontrolltermin>): Observable<Kontrolltermin> {
    return this.http.put<Kontrolltermin>(`${this.apiUrl}/kontrolltermin/${id}`, kontrolltermin);
  }

  // Delete kontrolltermin (soft delete)
  deleteKontrolltermin(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/kontrolltermin/${id}`);
  }

  // Get kontrolltermine by status
  getKontrolltermineByStatus(status: string): Observable<Kontrolltermin[]> {
    return this.http.get<Kontrolltermin[]>(`${this.apiUrl}/kontrolltermin/status/${status}`);
  }

  // Get statistics
  getStats(): Observable<any> {
    return this.http.get(`${this.apiUrl}/kontrolltermin-stats`);
  }

  // Get kontroll indikatoren
  getKontrollIndikatoren(
    kontrolltermin_id?: string,
    gebaeude_id?: string,
    sektion_id?: string,
    room_id?: string,
    befall?: number,
    befall_status_id?: number,
    checked_by?: string,
    checked_from?: string,
    checked_to?: string
  ): Observable<KontrollIndikator[]> {
    let params = new HttpParams();
    
    if (kontrolltermin_id) params = params.set('kontrolltermin_id', kontrolltermin_id);
    if (gebaeude_id) params = params.set('gebaeude_id', gebaeude_id);
    if (sektion_id) params = params.set('sektion_id', sektion_id);
    if (room_id) params = params.set('room_id', room_id);
    if (befall !== undefined) params = params.set('befall', befall.toString());
    if (befall_status_id !== undefined) params = params.set('befall_status_id', befall_status_id.toString());
    if (checked_by) params = params.set('checked_by', checked_by);
    if (checked_from) params = params.set('checked_from', checked_from);
    if (checked_to) params = params.set('checked_to', checked_to);

    return this.http.get<KontrollIndikator[]>(`${this.apiUrl}/kontroll-indikator`, { params });
  }

  // Get kontroll indikator by ID
  getKontrollIndikator(id: string): Observable<KontrollIndikator> {
    return this.http.get<KontrollIndikator>(`${this.apiUrl}/kontroll-indikator/${id}`);
  }

  // Create kontroll indikator
  createKontrollIndikator(indikator: Partial<KontrollIndikator>): Observable<KontrollIndikator> {
    return this.http.post<KontrollIndikator>(`${this.apiUrl}/kontroll-indikator`, indikator);
  }

  // Update kontroll indikator
  updateKontrollIndikator(id: string, indikator: Partial<KontrollIndikator>): Observable<KontrollIndikator> {
    return this.http.put<KontrollIndikator>(`${this.apiUrl}/kontroll-indikator/${id}`, indikator);
  }

  // Delete kontroll indikator
  deleteKontrollIndikator(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/kontroll-indikator/${id}`);
  }

  // Get kontroll indikatoren by kontrolltermin
  getKontrollIndikatorenByKontrolltermin(kontrolltermin_id: string): Observable<KontrollIndikator[]> {
    return this.http.get<KontrollIndikator[]>(`${this.apiUrl}/kontroll-indikator/kontrolltermin/${kontrolltermin_id}`);
  }

  // Bulk create kontroll indikatoren
  bulkCreateKontrollIndikatoren(kontrolltermin_id: string, indikatoren: Partial<KontrollIndikator>[]): Observable<KontrollIndikator[]> {
    return this.http.post<KontrollIndikator[]>(`${this.apiUrl}/kontroll-indikator/bulk`, {
      kontrolltermin_id,
      indikatoren
    });
  }

  // Get kontroll indikator statistics
  getKontrollIndikatorStats(kontrolltermin_id?: string): Observable<any> {
    let params = new HttpParams();
    if (kontrolltermin_id) params = params.set('kontrolltermin_id', kontrolltermin_id);
    
    return this.http.get(`${this.apiUrl}/kontroll-indikator-stats`, { params });
  }

  // Get freigaben
  getFreigaben(
    freigegeben?: number,
    kontrolltermin_id?: string,
    freigabe_durch?: string,
    freigabe_datum_from?: string,
    freigabe_datum_to?: string
  ): Observable<Freigabe[]> {
    let params = new HttpParams();
    
    if (freigegeben !== undefined) params = params.set('freigegeben', freigegeben.toString());
    if (kontrolltermin_id) params = params.set('kontrolltermin_id', kontrolltermin_id);
    if (freigabe_durch) params = params.set('freigabe_durch', freigabe_durch);
    if (freigabe_datum_from) params = params.set('freigabe_datum_from', freigabe_datum_from);
    if (freigabe_datum_to) params = params.set('freigabe_datum_to', freigabe_datum_to);

    return this.http.get<Freigabe[]>(`${this.apiUrl}/freigabe`, { params });
  }

  // Get freigabe by ID
  getFreigabe(id: string): Observable<Freigabe> {
    return this.http.get<Freigabe>(`${this.apiUrl}/freigabe/${id}`);
  }

  // Create freigabe
  createFreigabe(freigabe: Partial<Freigabe>): Observable<Freigabe> {
    return this.http.post<Freigabe>(`${this.apiUrl}/freigabe`, freigabe);
  }

  // Update freigabe
  updateFreigabe(id: string, freigabe: Partial<Freigabe>): Observable<Freigabe> {
    return this.http.put<Freigabe>(`${this.apiUrl}/freigabe/${id}`, freigabe);
  }

  // Delete freigabe
  deleteFreigabe(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/freigabe/${id}`);
  }

  // Approve freigabe
  approveFreigabe(id: string): Observable<Freigabe> {
    return this.http.post<Freigabe>(`${this.apiUrl}/freigabe/${id}/approve`, {});
  }

  // Reject freigabe
  rejectFreigabe(id: string, anmerkungen_freigabe?: string): Observable<Freigabe> {
    return this.http.post<Freigabe>(`${this.apiUrl}/freigabe/${id}/reject`, { anmerkungen_freigabe });
  }

  // Get freigabe by kontrolltermin
  getFreigabeByKontrolltermin(kontrolltermin_id: string): Observable<Freigabe> {
    return this.http.get<Freigabe>(`${this.apiUrl}/freigabe/kontrolltermin/${kontrolltermin_id}`);
  }

  // Get pending freigaben
  getPendingFreigaben(): Observable<Freigabe[]> {
    return this.http.get<Freigabe[]>(`${this.apiUrl}/freigabe-pending`);
  }

  // Get approved freigaben
  getApprovedFreigaben(): Observable<Freigabe[]> {
    return this.http.get<Freigabe[]>(`${this.apiUrl}/freigabe-approved`);
  }

  // Get freigabe statistics
  getFreigabeStats(): Observable<any> {
    return this.http.get(`${this.apiUrl}/freigabe-stats`);
  }

  // Helper methods for status and type labels
  getStatusLabel(status_id: number): string {
    const statuses = {
      1: 'Gelöscht',
      2: 'Abgebrochen',
      3: 'Fehlerhaft',
      4: 'Geplant',
      5: 'Aktuell',
      6: 'In Bearbeitung',
      7: 'Freigabe',
      8: 'Abgeschlossen'
    };
    return statuses[status_id as keyof typeof statuses] || 'Unbekannt';
  }

  getEinsatztypLabel(einsatztyp: string): string {
    const einsatztypen = {
      'kontrolle': 'Kontrolle',
      'notfall': 'Notfall'
    };
    return einsatztypen[einsatztyp as keyof typeof einsatztypen] || 'Unbekannt';
  }

  getBefallAmpelLabel(befall_status_id?: number): string {
    if (!befall_status_id) return 'Unbekannt';
    
    const ampel = {
      1: 'Grün',
      2: 'Orange',
      3: 'Rot'
    };
    return ampel[befall_status_id as keyof typeof ampel] || 'Unbekannt';
  }

  getBefallAmpelColor(befall_status_id?: number): string {
    if (!befall_status_id) return '#6c757d';
    
    const ampelColors = {
      1: '#28a745', // Grün
      2: '#ffc107', // Orange
      3: '#dc3545'  // Rot
    };
    return ampelColors[befall_status_id as keyof typeof ampelColors] || '#6c757d';
  }

  getBefallText(befall?: number): string {
    if (befall === 1) return 'Ja';
    if (befall === 0) return 'Nein';
    return 'Unbekannt';
  }

  // Generate PDF report for kontrolltermin
  generatePDFReport(kontrollterminId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/pdf-kontrolltermin/${kontrollterminId}`, {
      responseType: 'blob'
    });
  }

  // Accept kontrolltermin (activate job)
  acceptKontrolltermin(kontrollterminId: string, options?: { isAdmin?: boolean, forceActivation?: boolean }): Observable<{success: boolean, message?: string}> {
    const payload = {
      isAdmin: options?.isAdmin ?? false,
      forceActivation: options?.forceActivation ?? false
    };
    console.log('KontrollterminService: Sending accept request with payload:', payload);
    return this.http.post<{success: boolean, message?: string}>(`${this.apiUrl}/kontrolltermin/${kontrollterminId}/accept`, payload);
  }

  // Create or update freigabe
  createOrUpdateFreigabe(freigabeData: any): Observable<Freigabe> {
    return this.http.post<Freigabe>(`${this.apiUrl}/freigabe`, freigabeData);
  }

}
